import { IPCommandButton, IPSeparator, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { ExpressionReference } from 'components/KPI/Expression/expressionEditor.helpers';
import { Fragment, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

interface IExpressionSuggestionListProps {
  listId: string;
  items: ExpressionReference[];
  activeIndex: number;
  query: string;
  onInsert: (field: ExpressionReference) => void;
}

const emphasize = (value: string, query: string) => {
  const index = query ? value.toLowerCase().indexOf(query) : -1;
  if (index < 0) return value;

  return (
    <Fragment>
      {value.slice(0, index)}
      <IPText styles={{ root: { fontWeight: 600 } }}>{value.slice(index, index + query.length)}</IPText>
      {value.slice(index + query.length)}
    </Fragment>
  );
};

const ExpressionSuggestionList = (props: IExpressionSuggestionListProps) => {
  const { t } = useTranslation(['kpis']);
  const numericQuery = props.query !== '' && /^\d+$/.test(props.query);

  return (
    <IPStack>
      <IPStack.Item>
        <IPStack id={props.listId} role="listbox" aria-label={t('kpis:Fields.Expression')}>
          {props.items.map((field, index) => {
            const active = index === props.activeIndex;
            const matchedOnId =
              numericQuery &&
              !field.name.toLowerCase().includes(props.query) &&
              String(field.id).startsWith(props.query);
            const idText = String(field.id);

            return (
              <IPCommandButton
                key={field.id}
                id={`${props.listId}-${field.id}`}
                role="option"
                aria-selected={active}
                aria-label={field.name}
                tabIndex={-1}
                checked={active}
                iconProps={{ iconName: 'NumberField' }}
                onMouseDown={(event: MouseEvent<HTMLElement>) => {
                  event.preventDefault();
                  props.onInsert(field);
                }}
                onRenderText={() => (
                  <IPStack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} styles={{ root: { width: '100%' } }}>
                    <IPStack.Item grow styles={{ root: { minWidth: 0, textAlign: 'left' } }}>
                      <IPText>{matchedOnId ? field.name : emphasize(field.name, props.query)}</IPText>
                    </IPStack.Item>
                    {active && (
                      <IPStack.Item>
                        <IPText variant="small">{t('kpis:Expression.EnterInserts')}</IPText>
                      </IPStack.Item>
                    )}
                    <IPStack.Item>
                      <IPText variant="small">
                        {matchedOnId ? (
                          <Fragment>
                            {'['}
                            <IPText styles={{ root: { fontWeight: 600 } }}>{props.query}</IPText>
                            {idText.slice(props.query.length)}
                            {']'}
                          </Fragment>
                        ) : (
                          `[${field.id}]`
                        )}
                      </IPText>
                    </IPStack.Item>
                  </IPStack>
                )}
                styles={{
                  root: { height: 'auto', minHeight: 36, padding: '4px 8px', width: '100%' },
                  flexContainer: { width: '100%' },
                  textContainer: { flexGrow: 1, width: '100%' },
                }}
              />
            );
          })}
        </IPStack>
      </IPStack.Item>
      <IPSeparator />
      <IPStack.Item styles={{ root: { padding: '4px 8px' } }}>
        <IPText variant="small">{t('kpis:Expression.KeyboardLegend')}</IPText>
      </IPStack.Item>
    </IPStack>
  );
};

export default ExpressionSuggestionList;
